import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
// Types only: the library itself is fetched on demand in `loadHls()` so that the ~250 kB of hls.js
// stays out of the initial bundle. This service is reachable from CoreProvider, which every launch
// loads, but an external camera is optional and most sessions never attach one.
import type Hls from 'hls.js';
import type { BufferAppendingData, BufferCodecsData, ErrorData, SourceBufferName, Track } from 'hls.js';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { CoreProvider } from '../core.provider';
import { AssembledClip, CapturedFragment, assembleClip, isInitSegment } from './fmp4-clip.helper';
import { ensureSeekableMp4 } from '../local-data/mp4-duration.helper';

export type HlsCaptureStatus = 'idle' | 'connecting' | 'live' | 'error';

/**
 * How much already-watched media is kept behind the playhead while *not* recording.
 *
 * The press almost always lands in the middle of a segment, and that segment was appended well before
 * the press — hls.js downloads ahead of what is on screen. Without this retention the start of every
 * clip would be missing, and a take shorter than one segment would match no media at all. A couple of
 * segments' worth is enough, and it keeps idle memory in the low megabytes.
 */
const IDLE_RETENTION_SECONDS = 12;

/**
 * Longest clip either recording path will produce, in seconds.
 *
 * Exported because the component enforces the same ceiling — videojs-record's `maxLength` on the device
 * path, the capture ticker on this one — and the progress bar divides by it. Two copies of the number
 * would be two things to keep in step.
 */
export const MAX_CLIP_SECONDS = 60;

/**
 * Shortest window a take is allowed to ask for. Not a minimum clip length — the clip is however long
 * the fragments under the window turn out to be — but a floor that stops an instant tap from
 * describing a zero-length range that matches no media at all.
 */
const MIN_CLIP_SECONDS = 0.5;

/**
 * How far behind the live edge the preview may drift before we seek forward instead of letting the
 * latency controller catch up by playing faster. Comfortably more than the normal sync target, so
 * ordinary jitter never causes a visible jump.
 */
const MAX_LIVE_DRIFT_SECONDS = 8;

/**
 * How often the buffered-delay readout is refreshed. hls.js only touches the zone once per segment,
 * which is far too coarse for a number the user is watching to judge whether the feed is keeping up.
 */
const LATENCY_TICK_MS = 1000;

/**
 * How many fatal network errors are ridden out before the stream is declared dead.
 *
 * A blip on arena WiFi and a URL that is simply wrong look identical from here — hls.js exhausts its
 * own retries and reports one fatal error either way — so the only thing separating "reconnecting" from
 * "this will never work" is how many times it has already failed.
 */
const MAX_NETWORK_RETRIES = 4;

/** First backoff between those retries; each further attempt doubles it. */
const NETWORK_RETRY_BASE_MS = 1000;

/**
 * Records clips from an external camera that publishes HLS, without re-encoding anything.
 *
 * ## Why this exists next to the device-camera recorder rather than replacing it
 *
 * The existing path (`getUserMedia` → videojs-record → webm-wasm) captures the *device's own* camera.
 * This one captures a stream produced elsewhere — usually the match's live-broadcast crew, with the
 * HLS origin sitting in the cloud. The two share nothing upstream, but they converge completely
 * downstream: both hand a `Blob` to `FilesService.saveVideoToFilesystem()`, so the clip is stored,
 * served over `/static/` and uploaded to S3 exactly as it always was.
 *
 * ## One download, and only one
 *
 * The arena's uplink is the scarce resource here, so the stream must cross it once. hls.js is
 * therefore the *only* downloader: it drives the preview through MSE **and** hands us the fMP4 it just
 * transmuxed, via `BUFFER_CODECS` (the init segment) and `BUFFER_APPENDING` (the fragments). We keep a
 * copy of those bytes and cut clips out of them. No parallel fetcher, no second transmux, no
 * re-encoding — and quality adaptation comes from hls.js' own ABR, which reacts both to measured
 * bandwidth and to buffer stalls.
 */
@Injectable({
  providedIn: 'root'
})
export class HlsCaptureService {

  private core?: CoreProvider;

  private hls?: Hls;
  private video?: HTMLVideoElement;
  private sourceUrl?: string;

  public readonly status$ = new BehaviorSubject<HlsCaptureStatus>('idle');

  /**
   * Seconds the preview is behind the live edge — the delay held in buffer.
   *
   * Worth putting on screen: it is the difference between what the coach sees and what is happening on
   * court, so it tells them how stale the frame they are tagging is, and it grows visibly when the
   * arena connection starts struggling.
   */
  public readonly latencySeconds$ = new BehaviorSubject<number>(0);
  private latencyTicker?: any;

  /**
   * Which source buffer we mirror. hls.js reports `audiovideo` when the stream is muxed and it can use
   * a single buffer, and splits into `video`/`audio` otherwise. We follow whichever carries the
   * picture: a muxed stream therefore yields clips *with* sound at no extra cost, while a demuxed one
   * yields video only — interleaving two separate tracks into one MP4 is work this path does not do,
   * and silent clips are what the device-camera path has always produced anyway.
   */
  private trackName?: SourceBufferName;
  private initSegment?: Uint8Array;

  /** Recently appended fragments, newest last. Trimmed to {@link IDLE_RETENTION_SECONDS} when idle. */
  private fragments: CapturedFragment[] = [];

  private capturing = false;
  /** Media-timeline position of the frame on screen when the user pressed record. */
  private captureStart = 0;
  /** A live-edge resync that arrived mid-take and has to wait for it to finish. */
  private resyncPending = false;
  /** Teardown for the suspension listeners, so `detach` leaves nothing behind. */
  private lifecycleListeners: Array<() => void> = [];
  /**
   * ABR's manual level before a take pinned it — `-1` when ABR was choosing, which is the usual case.
   * Restored verbatim in {@link releaseLevel} so a take does not quietly convert a hand-picked level
   * into automatic. See `startCapture`.
   */
  private manualLevelBeforeCapture = -1;

  /** Fatal network errors since the last playlist that loaded, against {@link MAX_NETWORK_RETRIES}. */
  private networkRetries = 0;
  private networkRetryTimer?: ReturnType<typeof setTimeout>;

  /** Key of the fragment currently being appended, to tell a continued fragment from a re-delivered one. */
  private lastAppendedKey?: string;

  async initCore(core: CoreProvider) {
    this.core = core;
  }

  /**
   * Whether this device can use an external camera at all.
   *
   * Reading HLS needs Media Source Extensions, which the iOS WKWebView only exposes from 17.1 and only
   * as `ManagedMediaSource`. Where they are missing the option is hidden and the device camera remains
   * the only way to record, which still works.
   *
   * This deliberately mirrors `Hls.isSupported()` rather than calling it: the answer is needed
   * synchronously, from templates, on every launch — and pulling the library in just to ask would undo
   * the lazy loading below. Keep the two in step if hls.js ever changes its baseline probe.
   */
  public get isSupported(): boolean {
    // Cached because templates read this through `externalCameraEnabled` on every change detection
    // cycle, and `isTypeSupported` is not free. The answer cannot change within a session.
    if (this.supported === undefined) {
      const mediaSource: typeof MediaSource | undefined =
        (self as any).ManagedMediaSource ?? (self as any).MediaSource;
      this.supported = !!mediaSource
        && typeof mediaSource.isTypeSupported === 'function'
        && mediaSource.isTypeSupported('video/mp4;codecs="avc1.42E01E,mp4a.40.2"');
    }
    return this.supported;
  }
  private supported?: boolean;

  /** Constructor of the lazily fetched library, kept so a second attach does not re-download it. */
  private hlsCtor?: typeof Hls;

  private async loadHls(): Promise<typeof Hls> {
    if (!this.hlsCtor) this.hlsCtor = (await import('hls.js')).default;
    return this.hlsCtor;
  }

  public get isCapturing(): boolean {
    return this.capturing;
  }

  /** Seconds of media captured so far, for the `REC mm:ss` readout and the progress bar. */
  public get capturedSeconds(): number {
    if (!this.capturing || !this.video) return 0;
    return Math.max(0, this.video.currentTime - this.captureStart);
  }

  //#region Lifecycle
  /**
   * Point the preview element at `url` and start mirroring what hls.js buffers.
   *
   * Safe to call again with a different URL; the previous instance is torn down first.
   */
  public async attach(video: HTMLVideoElement, url: string): Promise<void> {
    this.detach();

    this.video = video;
    this.sourceUrl = url;
    this.status$.next('connecting');

    let HlsCtor: typeof Hls;
    try {
      HlsCtor = await this.loadHls();
    } catch (error) {
      // hls.js is a lazy chunk, so this is a network fetch and it can fail: an app updated while the
      // device was offline, a webview that dropped its cache. Nobody awaits this promise — the caller
      // is reached from a `setTimeout` — so without an end state here the rejection is swallowed and
      // the badge reads "Connecting…" over a black preview for the rest of the match.
      console.error('Could not load hls.js', error);
      if (this.sourceUrl === url) this.failStream();
      return;
    }
    // `detach()` may have run while the library was downloading — a fast source switch, or leaving the
    // tracker. Attaching now would leave an instance nobody owns, pulling the stream forever.
    if (this.sourceUrl !== url) return;

    const hls = new HlsCtor({
      // The preview is a few hundred pixels wide, but the *clip* is the deliverable and it must keep
      // the camera's real resolution. With this on, hls.js would size the level to the element and
      // every clip would be saved at the lowest variant.
      capLevelToPlayerSize: false,
      // Low-latency mode splits fragments into parts, which makes the buffered window harder to reason
      // about when cutting. A few seconds of delay costs nothing here — the clip is anchored to the
      // frame actually on screen, not to the live edge.
      lowLatencyMode: false,
      // Transmuxing off the main thread matters: this device is also running the tagging UI.
      enableWorker: true,
      // We keep our own copy of the media, so there is no reason to hold a long MSE buffer as well.
      maxBufferLength: 15,
      backBufferLength: 30,
      // --- staying on the live edge -------------------------------------------------------------
      // Normal target: three target durations behind the edge, the usual safety margin.
      liveSyncDurationCount: 3,
      // hls.js defaults this to Infinity, which means it *never* corrects latency: fall behind once —
      // a suspended tablet, a backgrounded tab — and it happily plays minutes-old footage forever.
      // Capping it makes the latency controller seek forward to the live sync point on its own.
      liveMaxLatencyDurationCount: 10,
      // For drift too small to justify a jump, catch up by playing slightly faster instead. A seek
      // would be a visible glitch; this is imperceptible.
      maxLiveSyncPlaybackRate: 1.5,
    });

    hls.on(HlsCtor.Events.BUFFER_CODECS, (_event, data: BufferCodecsData) => this.onCodecs(data));
    hls.on(HlsCtor.Events.BUFFER_APPENDING, (_event, data: BufferAppendingData) => this.onAppending(data));
    hls.on(HlsCtor.Events.MANIFEST_PARSED, () => {
      this.video?.play().catch(() => { /* autoplay refusal is not fatal — the user can tap play */ });
    });
    hls.on(HlsCtor.Events.LEVEL_LOADED, () => {
      // A playlist that loads is the only proof the stream is reachable, so it is also what clears the
      // retry budget: an arena connection that drops every ten minutes recovers every time.
      this.networkRetries = 0;
      if (this.status$.getValue() !== 'live') this.status$.next('live');
    });
    hls.on(HlsCtor.Events.ERROR, (_event, data: ErrorData) => this.onError(data));

    hls.loadSource(url);
    hls.attachMedia(video);
    this.hls = hls;
    this.watchForSuspension(video);

    this.latencyTicker = setInterval(() => {
      // Rounded before publishing so the readout does not flicker through insignificant digits, and so
      // the subject only emits when the displayed value actually changes.
      const latency = Math.round((this.hls?.latency ?? 0) * 10) / 10;
      if (this.latencySeconds$.getValue() !== latency) this.latencySeconds$.next(latency);
    }, LATENCY_TICK_MS);
  }

  public detach(): void {
    this.capturing = false;
    this.resyncPending = false;
    this.fragments = [];
    this.lastAppendedKey = undefined;
    this.initSegment = undefined;
    this.trackName = undefined;
    this.video = undefined;
    this.sourceUrl = undefined;

    this.networkRetries = 0;
    if (this.networkRetryTimer) {
      clearTimeout(this.networkRetryTimer);
      this.networkRetryTimer = undefined;
    }

    for (const remove of this.lifecycleListeners) remove();
    this.lifecycleListeners = [];

    if (this.latencyTicker) {
      clearInterval(this.latencyTicker);
      this.latencyTicker = undefined;
    }
    this.latencySeconds$.next(0);

    if (this.hls) {
      this.hls.destroy();
      this.hls = undefined;
    }
    this.status$.next('idle');
  }
  //#endregion

  //#region Staying on the live edge
  /**
   * Re-join the live edge whenever the platform has stopped playback behind our back.
   *
   * A backgrounded tab or a sleeping tablet freezes the media element while the broadcast keeps going.
   * On return the playhead is minutes behind, with only the handful of seconds that were buffered
   * before the interruption — so the coach is filming footage that has already happened. hls.js'
   * latency controller now corrects steady drift on its own (see `liveMaxLatencyDurationCount`), but
   * it needs playback running to do it, and after a suspension that is exactly what is missing.
   */
  private watchForSuspension(video: HTMLVideoElement): void {
    const onVisibility = () => { if (!document.hidden) this.resyncToLive(); };
    // Cordova's own lifecycle event: a tablet waking from sleep does not always fire visibilitychange
    // inside the webview, so the browser signal alone would miss the most common case on native.
    const onResume = () => this.resyncToLive();
    // Nothing in the UI can pause the preview — it has no controls — so a pause is always the
    // platform's doing, and always something to undo. Not while hidden, though: seeking a backgrounded
    // element only to be suspended again is churn, and `onVisibility` will handle it on the way back.
    const onPause = () => { if (!document.hidden) this.resyncToLive(); };

    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('resume', onResume);
    video.addEventListener('pause', onPause);

    this.lifecycleListeners = [
      () => document.removeEventListener('visibilitychange', onVisibility),
      () => document.removeEventListener('resume', onResume),
      () => video.removeEventListener('pause', onPause),
    ];
  }

  /**
   * Jump to the live sync point if we have drifted further than {@link MAX_LIVE_DRIFT_SECONDS}, and
   * get playback going again.
   *
   * Held back while a take is in flight: seeking forward mid-capture would splice the suspension's gap
   * straight into the clip. The jump happens as soon as the take ends instead.
   */
  private resyncToLive(): void {
    const video = this.video;
    if (!this.hls || !video) return;

    if (this.capturing) {
      this.resyncPending = true;
      return;
    }

    const target = this.liveEdge();
    if (target !== undefined && target - video.currentTime > MAX_LIVE_DRIFT_SECONDS) {
      console.log('Rejoining the live edge', { from: video.currentTime, to: target });
      video.currentTime = target;
      // Everything buffered belongs to where we just were, not to where we landed.
      this.fragments = [];
      this.lastAppendedKey = undefined;
    }

    if (video.paused) video.play().catch(() => { /* a refused resume is retried on the next signal */ });
  }

  /**
   * Where the live edge is, preferring hls.js' own sync point and falling back to the seekable range
   * for the window before the first live playlist has been parsed.
   */
  private liveEdge(): number | undefined {
    const syncPosition = this.hls?.liveSyncPosition;
    if (syncPosition !== null && syncPosition !== undefined && Number.isFinite(syncPosition)) return syncPosition;

    const seekable = this.video?.seekable;
    if (seekable?.length) return seekable.end(seekable.length - 1);
    return undefined;
  }

  /** Run a resync that was deferred because a take was in progress. */
  private flushPendingResync(): void {
    if (!this.resyncPending) return;
    this.resyncPending = false;
    this.resyncToLive();
  }
  //#endregion

  //#region Mirroring hls.js' output
  private onCodecs(data: BufferCodecsData): void {
    // hls.js announces a single `audiovideo` track for muxed sources and splits into `video`/`audio`
    // otherwise. The cast is needed because `BufferCodecsData` only declares the split case, while the
    // muxed key is very much emitted at runtime (see `buffer-controller`'s `audiovideo` handling).
    const tracks = data as Partial<Record<SourceBufferName, Track>>;
    const name: SourceBufferName | undefined = tracks.audiovideo ? 'audiovideo' : tracks.video ? 'video' : undefined;
    const track = name ? tracks[name] : undefined;
    if (!track?.initSegment || !name) return;

    // A level switch can re-announce codecs with a fresh init segment. Adopting the newest one is
    // right: it describes the fragments arriving from now on, which are the ones a clip will contain.
    this.trackName = name;
    this.initSegment = new Uint8Array(track.initSegment);
  }

  private onAppending(data: BufferAppendingData): void {
    if (!this.trackName || data.type !== this.trackName) return;
    if (this.capturing && this.capturedSeconds >= MAX_CLIP_SECONDS) return;
    // Already held from BUFFER_CODECS, and a second copy inside the media would corrupt the clip.
    if (isInitSegment(data.data)) return;

    const frag = data.frag;
    // Level *and* sequence number. HLS aligns sequence numbers across variants, so `sn` alone cannot
    // tell the segment arriving now from the same segment at the resolution ABR just switched away
    // from — and merging those two produces one entry holding the same moment twice, at two
    // resolutions, under a single init segment. `assembleClip` drops whatever survives from a
    // superseded level; this is what lets it see the difference.
    const key = `${frag.level}/${frag.sn}`;

    // hls.js hands the same fragment over in several pieces while it is still parsing, so appends are
    // accumulated per segment. `.slice()` because the underlying buffer comes back from the transmux
    // worker and we must not depend on hls.js leaving it alone once it has been appended.
    const chunk = data.data.slice();

    const existing = this.fragments.find(fragment => fragment.key === key);
    if (existing) {
      // The pieces of one fragment always arrive back to back. The same key turning up after a
      // different one means hls.js is re-delivering a segment it already appended — `startLoad()` after
      // a network error does exactly that — so the earlier bytes are superseded, not continued, and
      // keeping both would write the segment into the clip twice.
      if (this.lastAppendedKey === key) existing.chunks.push(chunk);
      else existing.chunks = [chunk];
      this.lastAppendedKey = key;
      return;
    }

    this.fragments.push({
      key,
      level: frag.level,
      start: frag.start,
      end: frag.start + frag.duration,
      chunks: [chunk],
    });
    this.lastAppendedKey = key;

    if (!this.capturing) this.trimIdleBuffer();
  }

  /**
   * Drop everything more than {@link IDLE_RETENTION_SECONDS} behind **the playhead**.
   *
   * Measuring from the newest buffered fragment instead would be wrong, and subtly so: hls.js reads
   * ahead of the playhead (`maxBufferLength`), so the newest fragment sits ~15 s in the future. A
   * retention window anchored there puts the cutoff *ahead* of what the user is watching and deletes
   * the very fragment a press is about to ask for — which showed up as short takes failing while long
   * ones, whose media arrives after the press, worked fine.
   *
   * Nothing ahead of the playhead is ever dropped: it has not been watched yet, and it is exactly what
   * the rest of a take will be made of.
   */
  private trimIdleBuffer(): void {
    const playhead = this.video?.currentTime ?? 0;
    const cutoff = playhead - IDLE_RETENTION_SECONDS;
    this.fragments = this.fragments.filter(fragment => fragment.end > cutoff);
  }
  //#endregion

  //#region Capture
  /**
   * Begin a take. Returns false when the stream is not delivering media yet, which the caller should
   * surface rather than leaving the user with a recording indicator over a dead stream.
   *
   * Quality is pinned here and released in {@link stopCapture}: ABR is free to move between clips, but
   * a switch *inside* a clip would change resolution partway through a single MP4, which some players
   * handle poorly. `loadLevel` is the setter to use — it fixes the level for subsequent fragment loads
   * without flushing what is already buffered, so the preview does not stall at the press.
   */
  public startCapture(): boolean {
    if (!this.hls || !this.video || !this.initSegment) return false;
    if (this.capturing) return true;

    this.captureStart = this.video.currentTime;
    this.capturing = true;

    // `loadLevel`'s getter reports the level being loaded, while its setter writes `manualLevel` — so
    // assigning the current value straight back is not the no-op it looks like: it is what switches ABR
    // off, pinned to exactly the variant already on screen. `manualLevel` is what ABR's *mode* was
    // before that (-1 for automatic), and it is what `releaseLevel` puts back.
    this.manualLevelBeforeCapture = this.hls.manualLevel;
    const currentLevel = this.hls.loadLevel;
    if (currentLevel >= 0) this.hls.loadLevel = currentLevel;

    return true;
  }

  /** Abandon the take without producing a clip, mirroring `onCancelRecording` on the camera path. */
  public cancelCapture(): void {
    if (!this.capturing) return;
    this.capturing = false;
    this.releaseLevel();
    this.trimIdleBuffer();
    this.flushPendingResync();
  }

  /**
   * End the take and assemble the clip.
   *
   * The window closes at the frame currently on screen rather than at the newest buffered fragment:
   * hls.js reads ahead of the playhead, and everything past what the user saw is footage they never
   * decided to keep.
   */
  public async stopCapture(): Promise<{ blob: Blob, durationMs: number } | null> {
    if (!this.capturing || !this.video || !this.initSegment) {
      this.cancelCapture();
      return null;
    }

    const windowStart = this.captureStart;
    // A quick tap — or a preview that stalled — can leave the playhead where it started, and a
    // zero-length window overlaps nothing, not even the fragment it landed in. Claiming a minimum
    // keeps the segment under the press, which is the footage the user meant.
    const windowEnd = Math.max(this.video.currentTime, this.captureStart + MIN_CLIP_SECONDS);
    const fragments = this.fragments;

    this.capturing = false;
    this.releaseLevel();

    const assembled: AssembledClip | null = assembleClip(this.initSegment, fragments, windowStart, windowEnd);
    this.trimIdleBuffer();
    // Only now that the clip is built is it safe to jump: the take has its bytes.
    this.flushPendingResync();

    if (!assembled) {
      console.warn('No buffered HLS media covered the capture window', {
        windowStart, windowEnd, buffered: fragments.length,
        range: fragments.length ? `${fragments[0].start}..${fragments[fragments.length - 1].end}` : 'empty',
      });
      return null;
    }

    // Written straight from a live `moov`, the clip would report a duration of zero and the bench could
    // neither seek in it nor draw a progress bar. Same problem, and same remedy, as the WebM path.
    const blob = await ensureSeekableMp4(assembled.blob, assembled.durationMs);
    return { blob, durationMs: assembled.durationMs };
  }

  /** Hand quality selection back to whatever was choosing it before the take — usually ABR. */
  private releaseLevel(): void {
    if (this.hls) this.hls.loadLevel = this.manualLevelBeforeCapture;
    this.manualLevelBeforeCapture = -1;
  }
  //#endregion

  //#region Errors
  /**
   * hls.js reports a great deal that is merely informational; only fatal errors need action, and two
   * of the three kinds are recoverable in place — reloading after a network blip is exactly what a
   * camera on arena WiFi needs.
   */
  private onError(data: ErrorData): void {
    if (!this.hls || !this.hlsCtor) return;

    // Non-fatal, but it is the exact symptom of returning from a suspended tab to a playhead the live
    // playlist has already rolled past: there is nothing left to play where we are standing.
    if (data.details === this.hlsCtor.ErrorDetails.BUFFER_STALLED_ERROR) {
      this.resyncToLive();
      return;
    }
    if (!data.fatal) return;

    if (data.type === this.hlsCtor.ErrorTypes.NETWORK_ERROR) {
      this.retryNetwork(data);
      return;
    }
    if (data.type === this.hlsCtor.ErrorTypes.MEDIA_ERROR) {
      console.warn('Fatal HLS media error, recovering', data.details);
      this.hls.recoverMediaError();
      return;
    }

    console.error('Unrecoverable HLS error', data.type, data.details);
    this.failStream();
  }

  /**
   * Reload after a fatal network error — but only so many times.
   *
   * A URL that is well formed and simply wrong (a 404 playlist, a host that is not there, an origin
   * serving no CORS headers) fails exactly like a dropped connection: hls.js exhausts its own retries
   * and reports one fatal error. Restarting the load unconditionally therefore loops forever on the
   * cases that can never succeed, and because `status$` never leaves `'connecting'` the user watches
   * "Connecting to the external camera…" for the rest of the match with nothing telling them their URL
   * is the problem. A bounded budget, cleared by the next playlist that loads, keeps real blips
   * recoverable while giving the hopeless cases an end state the UI can show.
   */
  private retryNetwork(data: ErrorData): void {
    if (this.networkRetries >= MAX_NETWORK_RETRIES) {
      console.error('Giving up on the external camera after repeated network errors', data.details);
      this.failStream();
      return;
    }

    // Backing off matters as much as the cap: a host that is down answers instantly, so retrying at
    // full speed burns the budget in milliseconds and gives a recovering connection no time to come up.
    const delay = NETWORK_RETRY_BASE_MS * 2 ** this.networkRetries;
    this.networkRetries++;
    console.warn('Fatal HLS network error, reloading', {
      details: data.details, attempt: this.networkRetries, delay,
    });

    if (this.networkRetryTimer) clearTimeout(this.networkRetryTimer);
    this.networkRetryTimer = setTimeout(() => {
      this.networkRetryTimer = undefined;
      if (!this.hls) return;
      this.hls.startLoad();
      // A connection that dropped long enough to be fatal has almost certainly outlived the segments
      // around the old playhead. Resuming there would stall again; the live edge is the only position
      // guaranteed to still exist in the playlist.
      this.resyncToLive();
    }, delay);
  }

  /**
   * Put the stream into its terminal state: stop pulling, say so, tell the user once.
   *
   * Also the reason `status$` can leave `'live'` — a stream that dies mid-match used to leave the badge
   * claiming the camera was still up.
   */
  private failStream(): void {
    if (this.networkRetryTimer) {
      clearTimeout(this.networkRetryTimer);
      this.networkRetryTimer = undefined;
    }
    // Nothing here is retryable any more, and hls.js will keep reloading the playlist on its own timer.
    this.hls?.stopLoad();

    if (this.status$.getValue() === 'error') return;
    this.status$.next('error');
    this.core?.errorToast(undefined, this.core.trans.instant(_('Could not connect to the external camera')));
  }
  //#endregion

  /** The stream currently attached, so the UI can show what it is recording from. */
  public get currentUrl(): string | undefined {
    return this.sourceUrl;
  }
}
